import moment from 'moment';

// 자연어 기간 파서 (월별 매출 순위 orchestration 공용 — SQL 파싱 실패 시 결정적 fallback) //
// 입력: userInput 문자열 + 기준일 { year, month, day } (현재 날짜)
// 출력: { start:'YYYY-MM-DD', end:'YYYY-MM-DD' } 또는 null(미검출 → 호출측 올해 default)
// 지원: '작년/재작년/올해/YYYY년' + 'N월', 'A부터 B까지', 단일 연/월, 연도만
export function parsePeriodRange(input, ref) {
  const text = typeof input === 'string' ? input : '';
  if (!text) return null;

  const curY = ref?.year;
  const curM = ref?.month;
  const curD = ref?.day;
  if (!curY) return null;

  const pad2 = (n) => String(n).padStart(2, '0');
  const today = `${curY}-${pad2(curM)}-${pad2(curD)}`;
  const lastDayOf = (yr, mo) => new Date(yr, mo, 0).getDate();

  const yearFromToken = (tok) => {
    if (!tok) return null;
    if (/재작년/.test(tok)) return curY - 2;
    if (/(작년|지난해|전년)/.test(tok)) return curY - 1;
    if (/(올해|금년|이번\s*해)/.test(tok)) return curY;
    const m = tok.match(/(\d{4})/);
    if (m) return Number(m[1]);
    // 2자리 연도 '23년' — 세기 pivot: 미래(curY+1 초과)면 1900대로 해석 (운영 6차 "23년 3월"→2026-03 바꿔치기 fix) //
    const m2 = tok.match(/(\d{2})\s*년/);
    if (m2) {
      const n = 2000 + Number(m2[1]);
      return n <= curY + 1 ? n : n - 100;
    }
    return null;
  };

  // "[연도토큰] N월" 세그먼트 수집 — 2자리 연도는 월 동반 시에만 연도로 인정 ('10년 회원권' 같은 기간 명사 오탐 차단) //
  const re = /(재작년|작년|지난해|전년|올해|금년|이번\s*해|\d{4}\s*년|\d{2}\s*년)?\s*(\d{1,2})\s*월/g;
  const segs = [];
  let mm;
  while ((mm = re.exec(text)) !== null) {
    segs.push({ yr: yearFromToken(mm[1]), month: Number(mm[2]) });
  }

  if (segs.length >= 2) {
    const s = segs[0];
    const e = segs[1];
    const sy = s.yr != null ? s.yr : curY;
    const ey = e.yr != null ? e.yr : sy; // 종료에 연도 토큰 없으면 시작 연도와 동일 가정
    if (s.month >= 1 && s.month <= 12 && e.month >= 1 && e.month <= 12) {
      return { start: `${sy}-${pad2(s.month)}-01`, end: `${ey}-${pad2(e.month)}-${pad2(lastDayOf(ey, e.month))}` };
    }
  }

  if (segs.length === 1 && segs[0].month >= 1 && segs[0].month <= 12) {
    const s = segs[0];
    const sy = s.yr != null ? s.yr : curY;
    return { start: `${sy}-${pad2(s.month)}-01`, end: `${sy}-${pad2(s.month)}-${pad2(lastDayOf(sy, s.month))}` };
  }

  // 월 없이 연도 토큰만 ('작년', 'YYYY년', '올해', 'NN년도') — bare 2자리 'NN년'은 기간 명사('10년 회원권')와 충돌해 미지원 //
  const yTokMatch = text.match(/(재작년|작년|지난해|전년|\d{4}\s*년|\d{2}\s*년도|올해|금년)/);
  if (yTokMatch) {
    const yr = yearFromToken(yTokMatch[1]);
    if (yr != null && yr < curY) return { start: `${yr}-01-01`, end: `${yr}-12-31` };
    if (yr === curY) return { start: `${curY}-01-01`, end: today };
  }

  return null;
}

// 기간별 매출 집계 + 링코페이 정산 예측 (통계 SalesPeriodBox / AI 공용 단일 소스) //
// 입력:
//   - startDate / endDate: moment 또는 ISO 문자열 (산정기간)
//   - yearSales: [{ approvalDate, totalPrice, cardPrice, merchantUid }]
//   - settlements: [{ settledDate, settlementAmount, status }]  (없으면 정산 예측만)
//   - settledMerchantUids: [uid...] (서버 정산 완료된 결제)
//   - settlementCycle: number (일)
//   - holidays: Set<'YYYY-MM-DD'> (주말+공휴일 보정용, 없으면 주말만 반영)
// 출력: { monthData(금액 내림차순), periodSalesPrice, settlementPrice, expectedSettlementPrice }
export function computeSalesPeriod({ startDate, endDate, yearSales, settlements, settledMerchantUids, settlementCycle, holidays }) {
  const sales = Array.isArray(yearSales) ? yearSales : [];
  const settlementList = Array.isArray(settlements) ? settlements : [];
  const holidaySet = holidays instanceof Set ? holidays : new Set(Array.isArray(holidays) ? holidays : []);
  const settledUidSet = new Set(settledMerchantUids || []);

  const start = moment(startDate);
  const end = moment(endDate);
  const cycle = settlementCycle || 0;

  const linkopayFeeRate = 0.035;

  // 주말/공휴일 체크 //
  function isOffDay(m) {
    const dow = m.day(); // 0=일, 6=토
    if (dow === 0 || dow === 6) return true;
    return holidaySet.has(m.format('YYYY-MM-DD'));
  }

  // 오늘 기준으로 몇 일 뒤가 영업일인지 //
  function daysUntilBusinessDay(m) {
    let d = m.clone();
    let days = 0;

    while (isOffDay(d)) {
      d.add(1, 'day');
      days++;
    }

    return days;
  }

  // 기간 내 일자 리스트 생성 //
  const dayList = [];
  let dayCursor = start.clone().startOf('day');
  const lastDay = end.clone().startOf('day');

  while (dayCursor.isSameOrBefore(lastDay, 'day')) {
    dayList.push({ date: dayCursor.clone(), settlementPrice: 0, settlementStatus: 'SCHEDULED' });
    dayCursor.add(1, 'day');
  }

  // 프론트 정산 예측 (merchantUid 없으면 예측 skip — AI 소스 방어) //
  for (let i = 0; i < dayList.length; i++) {
    for (let j = 0; j < sales.length; j++) {
      if (sales[j].merchantUid
        && dayList[i].date.clone().subtract(cycle, 'days').format('YYYY.MM.DD') === moment(sales[j].approvalDate).format('YYYY.MM.DD')
        && moment('2025-09-01').isBefore(moment(sales[j].approvalDate))
        && sales[j].cardPrice > 0
        && (sales[j].merchantUid.split('-')[2] === '1' || sales[j].merchantUid.split('-')[2] === '2')
        && !settledUidSet.has(sales[j].merchantUid)) {
          const feeAmount = Math.floor(sales[j].cardPrice * linkopayFeeRate);

          if (isOffDay(moment(dayList[i].date))) {
            const days = daysUntilBusinessDay(moment(dayList[i].date));

            if (dayList.length > (i + days)) dayList[i + days].settlementPrice += (sales[j].cardPrice - feeAmount);
          }
          else {
            dayList[i].settlementPrice += (sales[j].cardPrice - feeAmount);
          }
      }
    }
  }

  // 서버 정산 데이터로 프론트 예측 덮어쓰기 //
  for (let i = 0; i < dayList.length; i++) {
    for (let j = 0; j < settlementList.length; j++) {
      if (dayList[i].date.clone().format('YYYY.MM.DD') === moment(settlementList[j].settledDate).format('YYYY.MM.DD')) {
        dayList[i].settlementPrice = settlementList[j].settlementAmount;
        dayList[i].settlementStatus = settlementList[j].status;
        break;
      }
    }
  }

  let totalSettlementPrice = 0;
  let totalScheduledSettlementPrice = 0;

  for (const d of dayList) {
    if (d.settlementStatus === 'COMPLETED') totalSettlementPrice += d.settlementPrice;
    else totalScheduledSettlementPrice += d.settlementPrice;
  }

  // 월별 매출 집계 (빈 월 포함) //
  const monthMap = new Map();
  let monthCursor = start.clone().startOf('month');
  const lastMonth = end.clone().startOf('month');

  while (monthCursor.isSameOrBefore(lastMonth, 'month')) {
    monthMap.set(monthCursor.format('YYYY.MM'), { date: monthCursor.clone(), salesPrice: 0 });
    monthCursor.add(1, 'month');
  }

  let periodSalesPrice = 0;

  for (const s of sales) {
    const monthKey = moment(s.approvalDate).format('YYYY.MM');

    if (monthMap.has(monthKey)) {
      monthMap.get(monthKey).salesPrice += s.totalPrice;
      periodSalesPrice += s.totalPrice;
    }
  }

  // 매출 내림차순 정렬 //
  const monthData = Array.from(monthMap.values());
  monthData.sort((a, b) => b.salesPrice - a.salesPrice);

  return {
    monthData,
    periodSalesPrice,
    settlementPrice: totalSettlementPrice,
    expectedSettlementPrice: totalScheduledSettlementPrice,
  };
}
